import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Params, Router } from '@angular/router';
import { MaintenanceService, NO_INTERCEPT, WINDOW } from '@campus/browser';
import { CsrfService } from '@campus/dal';
import {
  ENVIRONMENT_API_TOKEN,
  ENVIRONMENT_ERROR_MANAGEMENT_FEATURE_TOKEN,
  EnvironmentErrorManagementFeatureAllowedErrorInterface,
} from '@campus/environment';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { AppShellService } from '../services';

export enum ErrorReason {
  ARCHIVE_PERSON_UNKNOWN = 'archive-person-unknown',
  WRONG_CHILD_CREDENTIALS = 'wrong-child-credentials',
}

@Injectable()
export class CampusHttpInterceptor implements HttpInterceptor {
  private router = inject(Router);
  private environmentApi = inject(ENVIRONMENT_API_TOKEN);
  private environmentErrorManagementFeature = inject(ENVIRONMENT_ERROR_MANAGEMENT_FEATURE_TOKEN);

  private maintenanceService = inject(MaintenanceService);
  private csrfService = inject(CsrfService);

  private window = inject(WINDOW);
  private isOnV2Route$: Observable<boolean> = inject(AppShellService).isOnV2Route$;

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isAPIRequest = request.url.startsWith(this.environmentApi.APIBase + '/api');
    const isMaintenancePage = this.router.routerState.snapshot.url.startsWith('/v2/error/503');
    const checkMaintenanceMode = this.environmentErrorManagementFeature?.runMaintenanceCheckInInterceptor;

    if (isMaintenancePage && isAPIRequest) {
      return throwError(new Error('The API request was blocked as the user is on the maintenance error page.'));
    }

    if (request.context.get(NO_INTERCEPT)) {
      return next.handle(request);
    }

    request = this.attachArchiveHeaderIfNeeded(request);

    // run this parallel to the request handler, because we don't want to slow down existing requests
    // often, the response will be cached and will not result in an HTTP call
    if (!isMaintenancePage && checkMaintenanceMode) {
      this.maintenanceService.isMaintenanceMode().subscribe((isInMaintenance) => {
        if (isInMaintenance) {
          this.router.navigate(['/v2/error', 503]);
        }
      });
    }

    return this.attachCsrfHeader(request).pipe(
      switchMap((requestWithCsrfToken) => next.handle(requestWithCsrfToken)),
      catchError((error: HttpErrorResponse) => {
        if (this.window.location.href.includes(this.environmentErrorManagementFeature.publicErrorRoute)) return;

        if (this.isManagedError(error) && !this.isAllowedError(error)) {
          // Log the error gracefully for developers without flooding Bugsnag
          this.logManagedError(error, request);

          this.isOnV2Route$.pipe(take(1)).subscribe((isOnV2Route) => {
            const queryParams = this.getQueryParamsForError(error);
            const nonPublicErrorRoute = isOnV2Route ? '/v2/error' : '/error';

            const errorRoute = this.isOnPublicRoute(request.url)
              ? this.environmentErrorManagementFeature.publicErrorRoute
              : nonPublicErrorRoute;

            this.router.navigate([errorRoute, error.status], {
              queryParams,
            });
          });

          // Return an empty observable to suppress the error since we're handling it with navigation
          // This prevents flooding Bugsnag with expected HTTP errors
          return EMPTY;
        }

        // For unmanaged or allowed errors, propagate them normally
        return throwError(() => error);
      })
    );
  }

  private isOnPublicRoute(route: string) {
    return this.environmentErrorManagementFeature.publicApiRoutes?.some((publicRoute) =>
      new RegExp(publicRoute, 'i').test(route)
    );
  }

  private getQueryParamsForError(error: HttpErrorResponse): Params {
    const queryParams: Params = {};

    const archiveYear = this.window.sessionStorage.getItem('archiveYear');
    if (error.status === 404 && archiveYear) {
      queryParams.reasonCode = ErrorReason.ARCHIVE_PERSON_UNKNOWN;
    }

    return queryParams;
  }

  private attachArchiveHeaderIfNeeded(request: HttpRequest<any>): HttpRequest<any> {
    const archiveYear = this.window.sessionStorage.getItem('archiveYear');
    if (!archiveYear) {
      return request;
    }

    return request.clone({
      headers: request.headers.set('x-archive', archiveYear),
    });
  }

  private attachCsrfHeader(request: HttpRequest<any>): Observable<HttpRequest<any>> {
    // if it's not a request to our API, skip token attachment entirely
    // We don't want to attach the header to APIs that will give us CORS errors like nestjs
    if (!request.url.startsWith(this.environmentApi.APIBase + '/api/')) {
      return of(request);
    }

    // CSRF token is only needed for state-modifying requests
    if (!['POST', 'PUT', 'PATCH', 'DELETE'].includes(request.method)) {
      return of(request);
    }

    return this.csrfService.getToken$().pipe(
      map((csrfToken) => {
        if (!csrfToken) {
          // shouldn't happen unless the api has no csrf-token endpoint
          // regardless, try to proceed without
          return request;
        }

        return request.clone({
          headers: request.headers.set('x-csrf-token', csrfToken),
        });
      })
    );
  }

  public isAllowedError(error: HttpErrorResponse): boolean {
    const isAllowed = this.environmentErrorManagementFeature.allowedErrors.some(
      (allowedError) =>
        checkProp(allowedError, 'status') &&
        checkProp(allowedError, 'name') &&
        checkProp(allowedError, 'statusText') &&
        checkUrlRegex(allowedError) &&
        checkMessageRegex(allowedError)
    );

    return isAllowed;

    function checkProp(
      allowedError: EnvironmentErrorManagementFeatureAllowedErrorInterface,
      prop: keyof EnvironmentErrorManagementFeatureAllowedErrorInterface
    ) {
      return !allowedError[prop] || allowedError[prop] === error[prop];
    }

    function checkUrlRegex(allowedError) {
      return !allowedError.urlRegex || new RegExp(allowedError.urlRegex, 'i').test(error.url);
    }

    function checkMessageRegex(allowedError) {
      return (
        !allowedError.messageRegex ||
        new RegExp(allowedError.messageRegex, 'i').test(error.message) ||
        (error.error &&
          error.error.error &&
          error?.error?.error?.message &&
          new RegExp(allowedError.messageRegex, 'i').test(error.error.error.message))
      );
    }
  }

  private isManagedError(error: HttpErrorResponse): boolean {
    return this.environmentErrorManagementFeature.managedStatusCodes.includes(error.status);
  }

  private logManagedError(error: HttpErrorResponse, request: HttpRequest<any>): void {
    const errorDetails = {
      type: 'HTTP_INTERCEPTOR_MANAGED_ERROR',
      status: error.status,
      statusText: error.statusText,
      url: error.url,
      method: request.method,
      message: error.message,
      timestamp: new Date().toISOString(),
    };

    // Use console.warn for managed errors (not console.error which might trigger error tracking)
    console.warn(`[Campus HTTP Interceptor] Managed ${error.status} error:`, errorDetails);
  }
}
